import { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { FaWifi, FaRedo, FaSearch,FaSearchPlus } from "react-icons/fa";
import TableSkeleton from './TableSkeleton'; // Import skeleton

// Services
import useSelectedDomain from "../service/Hooks/useSelectedDomain";
import { useLoader } from "../service/Hooks/useLoader";
import {
  getCorporateActionsPending,
  processCorporateAction,
  deleteEvent
} from "../service/api/eventService";

// Composants
import ConfirmationModal from "../Modal/ConfirmationModal";
import EventTablesRow from "../Events/EventTable/EventTablesRow";
import Pagination from "../Events/EventTable/Pagination";
import NonCashConfirmationModal from "../CorporateAction/NonCashConfirmationModal";
import CashDistributionModal from "../CorporateAction/CashDistributionModal";

const EVENTS_PER_PAGE = 10; // Standardisé
const DISTRIBUTION_MODAL_TYPES = {
  NON_CASH: 'non-cash',
  CASH_WITHOUT_TAX: 'cash-without-tax',
  CASH_WITH_TAX: 'cash-with-tax'
};

 // ==================== COMPOSANT INDICATEUR CONNEXION ====================
const ConnectionStatusBar = ({ isOffline, onRetry }) => {
  if (!isOffline) return null;
  
  return (
    <div className="connection-bar-ost">
      <div className="connection-content">
        <FaWifi className="connection-icon" />
        <span className="connection-text">Mode hors ligne - Les actions de distribution sont indisponibles</span>
        <button onClick={onRetry} className="connection-retry-btn">
          <FaRedo size={12} />
          Réessayer
        </button>
      </div>
    </div>
  );
};

// ==================== SKELETON INTELLIGENT ====================
const IntelligentSkeleton = () => (
  <div className="py-4">
    {/* Header avec recherche skeleton */}
    <div className="d-flex justify-content-between align-items-center mb-4 p-3 bg-white shadow-sm rounded">
      <div className="skeleton-title" style={{ 
        width: '120px', height: '32px', background: '#f0f0f0', borderRadius: '8px' 
      }} />
      <div className="skeleton-search" style={{ 
        width: '300px', height: '40px', background: '#f0f0f0', borderRadius: '25px' 
      }} />
    </div>
    
    {/* Table skeleton */}
    <TableSkeleton columns={8} rows={5} showHeader={true} />
  </div>
);

// ==================== HOOKS OPTIMISÉS ====================
const useEventData = (domainId, selectedYear) => {
  const [state, setState] = useState({
    events: [],
    filteredEvents: [],
    loading: true,
    initialLoading: true,
    error: null,
    isOffline: false
  });

  const isConnectionError = useCallback((err) => {
    if (!err) return false;
    return !err.response || 
           err.code === 'ECONNABORTED' || 
           err.message?.includes('Network Error') ||
           err.message?.includes('Failed to fetch') ||
           err.message?.includes('connexion') ||
           err.message?.includes('network');
  }, []);

  const fetchEvents = useCallback(async () => {
    if (!domainId) {
      setState(prev => ({ ...prev, loading: false, initialLoading: false }));
      return;
    }

    setState(prev => ({ 
      ...prev, 
      loading: true, 
      error: null, 
      isOffline: false,
      initialLoading: prev.events.length === 0
    }));

    try {
      const response = await getCorporateActionsPending(domainId);
      const events = response.data || [];
      
      const filteredEvents = selectedYear 
        ? events.filter(event => {
            if (event.creationDate) {
              const eventYear = new Date(event.creationDate).getFullYear();
              return eventYear === selectedYear;
            }
            return false;
          })
        : events;

      setState(prev => ({ // 🔧 Utiliser prev.isOffline
        events,
        filteredEvents,
        loading: false,
        initialLoading: false,
        error: null,
        isOffline: false
      }));
      
      // 🔧 Vérifier si on était offline avant
      if (state.isOffline) {
        toast.success("Connexion rétablie", { duration: 2000 });
      }
    } catch (err) {
      if (isConnectionError(err)) {
        setState(prev => ({
          ...prev,
          loading: false,
          initialLoading: false,
          error: null,
          isOffline: true,
          filteredEvents: prev.filteredEvents.length > 0 ? prev.filteredEvents : [],
          events: prev.events.length > 0 ? prev.events : []
        }));
      } else {
        setState({
          events: [],
          filteredEvents: [],
          loading: false,
          initialLoading: false,
          error: "Erreur lors de la récupération des événements.",
          isOffline: false
        });
      }
    }
  }, [domainId, selectedYear, isConnectionError]); // 🔧 Retirer state.isOffline des dépendances

  return { ...state, refetch: fetchEvents };
};

// ==================== PAGINATION ====================
const usePagination = (data, itemsPerPage) => {
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = Math.ceil((data?.length || 0) / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = data?.slice(indexOfFirstItem, indexOfLastItem) || [];

  const handlePageChange = useCallback((pageNumber) => {
    if (typeof pageNumber === 'number' && pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  }, [totalPages]);

  useEffect(() => {
    setCurrentPage(1);
  }, [data?.length]);

  return {
    currentPage,
    totalPages,
    currentItems,
    handlePageChange
  };
};

// ==================== ÉTATS ====================
const LoadingState = () => <IntelligentSkeleton />;

const ErrorState = ({ error, onRetry }) => (
  <div className="text-center py-8 p-4 border rounded-3 bg-danger-subtle">
    <i className="bi bi-exclamation-triangle-fill fs-1 text-danger mb-3" />
    <h5 className="fw-bold mb-3 text-danger">{error}</h5>
    <button className="btn btn-primary rounded-pill px-4" onClick={onRetry}>
      <FaRedo className="me-2" /> Réessayer
    </button>
  </div>
);

const EmptyState = ({ selectedYear, isOffline }) => (
  <div className="text-center py-8">
    <div className="table-responsive">
      <table className="table table-sm">
        <thead className="table-light">
          <tr>
            <th>Type</th><th>Montant</th><th>Nombre titre</th><th>Capital (%)</th>
            <th>Titre</th><th>ISIN</th><th>Marché</th><th>Échéance</th>
            <th>Statut</th><th className="text-center">Distribuer</th><th className="text-center">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td colSpan={11} className="py-8 text-center">
              <div className="text-muted">
                <FaSearchPlus className="fs-1 opacity-50 d-block mb-4 mx-auto" /> {/* 🔧 Correction */}
                <h5 className="fw-bold mb-3">
                  {isOffline 
                    ? "Mode hors ligne" 
                    : selectedYear 
                      ? `Aucun OST en attente pour ${selectedYear}`
                      : "Aucun OST en attente"
                  }
                </h5>
                <p className="mb-4">
                  {isOffline 
                    ? "Données en cache disponibles"
                    : "Commencez par créer une opération OST"
                  }
                </p>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
);

const TableHeader = ({ selectedYear, filteredCount, isOffline }) => (
  <div className="card-header d-flex justify-content-between align-items-center">
    <h4 className="card-title fw-bold text-primary-emphasis text-uppercase fs-2" 
        style={{ letterSpacing: "0.10em" }}>
      OST en attente
    </h4>
    <div className="d-flex align-items-center gap-3">
      {selectedYear && (
        <span className="badge bg-primary-subtle text-primary rounded-pill px-3 py-1">
          Année {selectedYear}
        </span>
      )}
      <small className={`fw-semibold ${isOffline ? 'text-warning' : 'text-muted'}`}>
        {filteredCount} résultat{filteredCount !== 1 ? 's' : ''}
        {isOffline && ' (hors ligne)'}
      </small>
    </div>
  </div>
);

// ==================== COMPOSANT PRINCIPAL ====================
const TableDashboard = ({ 
  refresh, 
  selectedYear,
  onEditEvent, 
  onDetailsEvent,
}) => {
  const selectedDomain = useSelectedDomain();
  const { showLoader, hideLoader } = useLoader();
  
  const [eventToDelete, setEventToDelete] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [distributionModal, setDistributionModal] = useState({
    show: false,
    type: null,
    event: null,
    withTaxField: false
  });

  // 🔧 Données
  const { 
    filteredEvents, 
    loading, 
    initialLoading, 
    error, 
    isOffline, 
    refetch 
  } = useEventData(selectedDomain?.id, selectedYear);

  // 🔧 Recherche
  const searchedEvents = useMemo(() => {
    if (!searchQuery.trim()) return filteredEvents;
    const query = searchQuery.toLowerCase().trim();
    return filteredEvents.filter(event => 
      event.type?.toLowerCase().includes(query) ||
      event.securityName?.toLowerCase().includes(query) ||
      event.targetMarket?.toLowerCase().includes(query)
    );
  }, [filteredEvents, searchQuery]);

  // 🔧 Pagination sur les résultats filtrés
  const { 
    currentPage, 
    totalPages, 
    currentItems: displayEvents, 
    handlePageChange 
  } = usePagination(searchedEvents, EVENTS_PER_PAGE);

  // 🔧 useEffect pour refetch (pas de hooks à l'intérieur!)
  useEffect(() => {
    refetch();
  }, [selectedDomain?.id, refresh, selectedYear, refetch]);

  // 🔧 Gestion reconnexion
  useEffect(() => {
    const handleOnline = () => {
      if (isOffline) {
        refetch();
      }
    };
    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [isOffline, refetch]);

  // ==================== HANDLERS ====================
  const handleRetry = useCallback(() => refetch(), [refetch]);

  // 🔧 Fermeture du modal de distribution
  const closeDistributionModal = useCallback(() => {
    setDistributionModal({
      show: false,
      type: null,
      event: null,
      withTaxField: false
    });
  }, []);

  // 🔧 Handlers de distribution
  const handleDistributeClick = useCallback((event, withTaxField = false) => {
    if (isOffline || !event) {
      toast.error("Distribution impossible en mode hors ligne");
      return;
    }

    let modalType;
    if (event.type === 'DISTRIBUTION_NON_CASH') {
      modalType = DISTRIBUTION_MODAL_TYPES.NON_CASH;
    } else if (withTaxField) {
      modalType = DISTRIBUTION_MODAL_TYPES.CASH_WITH_TAX;
    } else {
      modalType = DISTRIBUTION_MODAL_TYPES.CASH_WITHOUT_TAX;
    }

    setDistributionModal({
      show: true,
      type: modalType,
      event,
      withTaxField
    });
  }, [isOffline]);

  // 🔧 Confirmation distribution
  const handleConfirmDistribution = useCallback(async (taxPercentage) => {
    if (!distributionModal.event) return;

    showLoader();
    try {
      await processCorporateAction(
        distributionModal.event.id,
        taxPercentage
      );
      toast.success("Distribution effectuée avec succès");
      closeDistributionModal();
      refetch();
    } catch (err) {
      toast.error("Erreur lors de la distribution");
      console.error(err);
    } finally {
      hideLoader();
    }
  }, [distributionModal.event, showLoader, hideLoader, refetch, closeDistributionModal]);

  // 🔧 Handlers d'édition/détails/suppression
  const handleEditClick = useCallback((event) => {
    if (onEditEvent) onEditEvent(event);
  }, [onEditEvent]);

  const handleDetailsClick = useCallback((event) => {
    if (onDetailsEvent) onDetailsEvent(event);
  }, [onDetailsEvent]);

  const handleDeleteClick = useCallback((event) => {
    setEventToDelete(event);
    setShowDeleteModal(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!eventToDelete) return;
    
    showLoader();
    try {
      await deleteEvent(eventToDelete.id);
      toast.success("Événement supprimé");
      setShowDeleteModal(false);
      setEventToDelete(null);
      refetch();
    } catch (err) {
      toast.error("Erreur lors de la suppression");
      console.error(err);
    } finally {
      hideLoader();
    }
  }, [eventToDelete, showLoader, hideLoader, refetch]);

  const handleCancelDelete = useCallback(() => {
    setShowDeleteModal(false);
    setEventToDelete(null);
  }, []);

  // ==================== RENDU ====================
  const displayCount = searchedEvents.length;

  const states = {
    showInitialLoading: initialLoading,
    showSkeleton: loading && !initialLoading,
    showError: error && !loading,
    showEmpty: !loading && !error && displayCount === 0,
    showData: !loading && !error && displayCount > 0
  };

  return (
    <>
      <ConnectionStatusBar isOffline={isOffline} onRetry={handleRetry} />
      
      <div className="row">
        <div className="col-lg-12">
          <div className={`card ${isOffline ? 'opacity-75' : ''}`}>
            <TableHeader 
              selectedYear={selectedYear} 
              filteredCount={displayCount}
              isOffline={isOffline} 
            />
            
            <div className="card-body p-0">
              {/* Barre de recherche */}
              <div className="p-3 border-bottom bg-light">
                <div className="position-relative">
                  <FaSearch className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" />
                  <input
                    className="form-control ps-5"
                    placeholder="Rechercher par type, titre ou marché..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    disabled={isOffline || loading}
                  />
                </div>
              </div>

              {/* États intelligents */}
              {states.showInitialLoading ? (
                <LoadingState />
              ) : states.showSkeleton ? (
                <div className="p-4">
                  <TableSkeleton columns={11} rows={5} showHeader={false} />
                </div>
              ) : states.showError ? (
                <ErrorState error={error} onRetry={handleRetry} />
              ) : states.showEmpty ? (
                <EmptyState selectedYear={selectedYear} isOffline={isOffline} />
              ) : (
                <>
                  <div className="table-responsive">
                    <table className="table table-sm table-striped table-hover mb-0">
                      <thead className="table-light sticky-top">
                        <tr>
                          <th>Type</th><th>Montant</th><th>Nombre titre</th>
                          <th>Capital (%)</th><th>Titre</th><th>ISIN</th>
                          <th>Marché</th><th>Échéance</th><th>Statut</th>
                          <th className="text-center">Distribuer</th>
                          <th className="text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {displayEvents.map((event) => (
                          <EventTablesRow
                            key={event.id}
                            event={event}
                            onEdit={handleEditClick}
                            onDelete={handleDeleteClick}
                            onDetails={handleDetailsClick}
                            onDistributeClick={handleDistributeClick}
                            isOffline={isOffline}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="p-3 border-top bg-light">
                      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <small className="text-muted">
                          {((currentPage - 1) * EVENTS_PER_PAGE) + 1}-
                          {Math.min(currentPage * EVENTS_PER_PAGE, displayCount)} 
                          sur {displayCount} 
                          {searchQuery && ` (recherche: "${searchQuery}")`}
                        </small>
                        <Pagination
                          currentPage={currentPage}
                          totalPages={totalPages}
                          onPageChange={handlePageChange}
                          ariaLabel="Pagination OST"
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {!isOffline && showDeleteModal && eventToDelete && (
        <ConfirmationModal
          message={`Supprimer l'événement #${eventToDelete.id} (${eventToDelete.type}) ?`}
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
        />
      )}

      <NonCashConfirmationModal
        show={!isOffline && distributionModal.show && distributionModal.type === DISTRIBUTION_MODAL_TYPES.NON_CASH}
        onHide={closeDistributionModal}
        onConfirm={() => handleConfirmDistribution(null)}
        event={distributionModal.event}
        loading={false}
      />

      <CashDistributionModal
        show={!isOffline && distributionModal.show && distributionModal.type === DISTRIBUTION_MODAL_TYPES.CASH_WITHOUT_TAX}
        onHide={closeDistributionModal}
        onConfirm={() => handleConfirmDistribution(null)}
        event={distributionModal.event}
        loading={false}
        withTaxField={false}
      />

      <CashDistributionModal
        show={!isOffline && distributionModal.show && distributionModal.type === DISTRIBUTION_MODAL_TYPES.CASH_WITH_TAX}
        onHide={closeDistributionModal}
        onConfirm={handleConfirmDistribution}
        event={distributionModal.event}
        loading={false}
        withTaxField={true}
      />
    </>
  );
};

export default TableDashboard;